import React from "react";
import Helmet from "react-helmet";
import { Link } from "react-router-dom";
import { Navbar2, Footer2 } from "../../components";
import SeriesNav from "../../components/labs/SeriesNav";
import Zoomable from "../../components/Zoomable/Zoomable";
import { getRouteMeta } from "../../seoData";
import gpoList from "../../images/labs/gpo-list.png";
import gpoLogonScriptPath from "../../images/labs/gpo-logon-script-path.png";

const SCRIPT = `#Get current username and computer name
$currentuser = $env:USERNAME.ToLower()
$currentPC = $env:COMPUTERNAME.ToLower()

#Get current user's groups
$groups = (whoami /groups) -join ""

#Rule 1: if user is a Domain Admin, allow login
if ($groups -match "Domain Admins") {
    exit 0
}

#Rule 2: if the computer name contains the username, allow login
if ($currentPC -like "*$currentuser*") {
    exit 0
}
else {
    #otherwise, log the user off
    shutdown.exe /l
}

<# Stricter alternative, not enabled yet: deny immediately with a
   logon failure instead of letting the user log in and then kicking
   them back off. #>
# Write-Output "Logon not allowed"
# exit 1`;

function DigitalForensicsHomelabGpos() {
  const { title, description: desc, image, url } = getRouteMeta(
    "/digital-forensics-homelab-gpos"
  );

  return (
    <div>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} data-react-helmet="true"></meta>

        <meta property="og:type" content="website" data-react-helmet="true"></meta>
        <meta property="og:url" content={url} data-react-helmet="true"></meta>
        <meta property="og:title" content={title} data-react-helmet="true"></meta>
        <meta property="og:description" content={desc} data-react-helmet="true"></meta>
        <meta property="og:image" content={image} data-react-helmet="true"></meta>

        <meta property="twitter:card" content="summary_large_image"></meta>
        <meta property="twitter:url" content={url} data-react-helmet="true"></meta>
        <meta property="twitter:title" content={title} data-react-helmet="true"></meta>
        <meta property="twitter:description" content={desc} data-react-helmet="true"></meta>
        <meta property="twitter:image" content={image} data-react-helmet="true"></meta>
      </Helmet>
      <Navbar2 />
      <section id="blogs">
        <div className="blogs main">
          <div className="blogs-header pb-4">
            <Link to="/projects/" className="blog-back-link">
              <i className="fa-solid fa-arrow-left"></i>&nbsp;Back to Projects
            </Link>
            <h2 className="section-title">Digital Forensics HomeLab</h2>
            <div className="blog-meta">
              <span>
                <i className="fa-solid fa-code"></i>Part 5: Group Policy
                Objects
              </span>
            </div>
          </div>

          <SeriesNav current="/digital-forensics-homelab-gpos/" />

          <p className="m-5">
            Part 4 was about SYSVOL as a concept. This part is where that
            concept turns into something I actually built: a Group Policy
            Object that enforces which user is allowed to log into which
            lab machine.
          </p>

          <h3>What GPOs Are, and What Gets Replicated</h3>
          <p className="m-5">
            A Group Policy Object is a set of settings linked to a domain
            or an OU that gets applied to the computers and users inside
            it. Every GPO actually has two halves. The Group Policy
            Container lives inside Active Directory itself and holds
            metadata and versioning. The Group Policy Template is the half
            that lives in SYSVOL, the actual settings, registry values, and
            any scripts the policy references. That second half, the GPT,
            is what DFS Replication is copying between domain controllers
            every time a GPO changes. On ZINGDC1, Group Policy Management
            currently shows three GPOs: the built-in{" "}
            <span className="fw-bolder">Default Domain Policy</span> and{" "}
            <span className="fw-bolder">
              Default Domain Controllers Policy
            </span>
            , and one I built myself,{" "}
            <span className="fw-bolder">Enforce_User_Pc_match</span>.
          </p>

          <div className="screenshot-frame">
            <Zoomable label="Group Policy Objects in zingnet.local">
              <img
                src={gpoList}
                width="1500"
                height="187"
                alt="Group Policy Management showing three GPOs: Default Domain Controllers Policy, Default Domain Policy, and Enforce_User_Pc_match"
              />
            </Zoomable>
          </div>
          <span className="screenshot-caption">
            The three GPOs currently in zingnet.local
          </span>

          <h3>How to Create a GPO</h3>
          <p className="m-5">
            From Group Policy Management on the DC, I right-clicked the{" "}
            <span className="fw-bolder">Group Policy Objects</span>{" "}
            container, chose New, and named it{" "}
            <span className="fw-bolder">Enforce_User_Pc_match</span>. That
            creates the GPO unlinked, it doesn't do anything until it's
            actually applied somewhere. Editing it opens the Group Policy
            Management Editor, where the real settings and scripts get
            configured. The last step, and the one I almost skipped, is
            linking it to the right OU. I linked mine to{" "}
            <span className="fw-bolder">LabEnv</span> specifically instead
            of the domain root, so it only ever touches the lab objects
            from Part 3 and can't accidentally affect anything else in the
            domain.
          </p>

          <h3>Building My User Enforcement Script</h3>
          <p className="m-5">
            The idea was simple: each lab user (User One through User
            Five) should only be able to log into the machine that matches
            them, and anyone who isn't should get logged straight back
            out. It's a small exercise, but it's practice in the kind of
            thinking that matters for DFIR, an account logging into a
            machine it has no business being on is exactly the kind of
            anomaly you'd want to catch.
          </p>
          <p className="m-5">
            The script is wired in through{" "}
            <span className="fw-bolder">Enforce_User_Pc_match</span> under
            Computer Configuration, Policies, Windows Settings, Scripts
            (Logon/Logoff), as a PowerShell logon script. Because it's a
            GPO logon script, it lives in SYSVOL, the exact thing Part 4
            was about, at{" "}
            <span className="fw-bolder">
              \\zingnet.local\SYSVOL\zingnet.local\scripts\Check_user.ps1
            </span>
            .
          </p>

          <div className="screenshot-frame">
            <Zoomable label="Logon script configured to run from SYSVOL">
              <img
                src={gpoLogonScriptPath}
                width="1210"
                height="757"
                alt="Group Policy Management Editor showing the PowerShell logon script pointed at the SYSVOL scripts path"
              />
            </Zoomable>
          </div>
          <span className="screenshot-caption">
            The logon script pointed at its SYSVOL path
          </span>

          <pre>
            <code>{SCRIPT}</code>
          </pre>
          <p className="m-5">
            The logic is two rules. Domain Admins are always let through,
            since I still need to be able to log into any machine to
            administer the lab. Otherwise, it checks whether the computer
            name contains the current username, USER1 and its matching
            user get through, anyone else gets logged off with{" "}
            <span className="fw-bolder">shutdown.exe /l</span>. Right now
            that's a soft enforcement, the user does briefly log in before
            getting kicked back out. There's a stricter version commented
            out that would deny the logon outright instead, I just haven't
            flipped it on yet while I'm still testing that the matching
            logic behaves the way I expect across all five test accounts.
          </p>

          <SeriesNav current="/digital-forensics-homelab-gpos/" />
        </div>
        <Footer2 />
      </section>
    </div>
  );
}

export default DigitalForensicsHomelabGpos;
